import { useTranslation } from "react-i18next";
import { formatDateBR } from "@/lib/date-utils";
import type { StayPlanPaymentStatus } from "@/lib/daycare-hotel/stayPlans";

export interface StayPlanPaymentBadgeProps {
  status: StayPlanPaymentStatus;
  dueOn: string | null;
  amount: number | null;
  className?: string;
}

const TONE: Record<Exclude<StayPlanPaymentStatus, "none">, string> = {
  paid: "bg-emerald-700 text-white",
  scheduled: "bg-amber-100 text-amber-900",
  awaiting: "bg-amber-600 text-white",
  overdue: "bg-destructive text-destructive-foreground",
};

function formatAmount(value: number, language: string) {
  return new Intl.NumberFormat(language.startsWith("pt") ? "pt-BR" : "en-US", {
    style: "currency",
    currency: "BRL",
  }).format(value);
}

export function StayPlanPaymentBadge({ status, dueOn, amount, className = "" }: StayPlanPaymentBadgeProps) {
  const { t, i18n } = useTranslation("daycareHotel");

  if (status === "none") {
    return <span className={`inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 text-xs font-semibold text-muted-foreground ${className}`}>{t("plans.payment.open")}</span>;
  }

  const date = dueOn ? formatDateBR(dueOn) : "";
  const base = t(`plans.payment.${status}`, { date });
  const money = amount !== null ? formatAmount(amount, i18n.language) : null;
  const label = money ? t("plans.payment.withAmount", { label: base, amount: money }) : base;
  const classes = `inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${TONE[status]} ${className}`;

  return <span className={classes}>{label}</span>;
}
